import classNames from 'classnames';
import { when } from 'mobx';
import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { ISegment, Segment } from '@feathr/blackbox';
import { ensurePredicateKeys } from '@feathr/blackbox';
import type { IAsyncSelectProps } from '@feathr/components';
import { UniversalSelect } from '@feathr/components';
import { useStore } from '@feathr/extender/state';
import type { IListParams } from '@feathr/rachis';

import SegmentMenuList from './SegmentMenuList';
import type { TStat } from './SegmentOption';
import SegmentOption from './SegmentOption';
import SegmentSingleValue from './SegmentSingleValue';

import * as styles from './SegmentSelectSimple.css';

export interface ISegmentSelectSimpleChangeProps {
  id: string;
  oldId?: string;
}

export interface ISegmentSelectSimpleProps
  extends Omit<
    IAsyncSelectProps<Segment>,
    'value' | 'onSelectMulti' | 'onSelectSingle' | 'onClear' | 'onChange'
  > {
  wrapperClassName?: string;
  filters?: IListParams<ISegment>['filters'];
  disabled?: boolean;
  excludeIds?: string[];
  hideCreate?: boolean;
  includeIds?: string[];
  onChange: (props: ISegmentSelectSimpleChangeProps) => void;
  onCreate?: (segment: Segment) => Promise<void> | void;
  stat?: TStat;
  name?: string;
  value?: string;
}

function SegmentSelectSimple({
  filters = {},
  disabled = false,
  excludeIds = [],
  hideCreate = false,
  includeIds = [],
  onChange,
  onCreate,
  value,
  wrapperClassName,
  ...additionalProps
}: Readonly<ISegmentSelectSimpleProps>): JSX.Element {
  const { Segments } = useStore();
  const [, setSegmentQuery] = useState('');
  const { t } = useTranslation();
  // Read from additionalProps so `stat` still flows through to selectProps via the spread.
  const { stat } = additionalProps;
  /*
   * Bumped after creating a group to remount the select, which closes the
   * open menu (the create button lives in a custom MenuList and does not
   * trigger react-select's default close-on-select).
   */
  const [menuResetKey, setMenuResetKey] = useState(0);

  async function loadSegmentOptions(inputValue: string): Promise<Segment[]> {
    const localFilters: IListParams<ISegment>['filters'] = {
      ...filters,
      is_archived__ne: true,
      name__icontains: inputValue,
    };

    const segments = Segments.list(
      {
        filters: localFilters,
        pagination: {
          page: 0,
          page_size: 20,
        },
      },
      {},
    );
    await when(() => !segments.isPending);
    // Converting observables back to vanilla JavaScript.
    return [...segments.models.slice()];
  }

  function filterOption(option: Segment): boolean {
    if (hideCreate && option.id === 'addSegment') {
      return false;
    }
    if (includeIds.length) {
      return includeIds.includes(option.id) && !excludeIds.includes(option.id);
    }
    return !excludeIds.includes(option.id);
  }

  function handleSelect(option: Segment): void {
    if (!option.id) {
      // This should not happen.
      return;
    }
    onChange({
      id: option.id,
      oldId: value,
    });
  }

  function handleInputChange(newValue: string): void {
    /*
     * Force loadOptions to refresh when input changes.
     * If the user clicks the input and does not select anything
     * provide a value of the last action to trigger the refresh.
     */
    setSegmentQuery(newValue);
  }

  const segment = value ? Segments.get(value) : undefined;

  async function handleCreateSegment(): Promise<void> {
    const newSegment = Segments.create({
      name: stat === 'num_emails_total' ? t('New Email Group') : undefined,
      mode: 'match_any',
      lookback_mode: 'unbounded',
      predicates:
        stat === 'num_emails_total'
          ? [
              ensurePredicateKeys({
                kind: 'attribute',
                attr_against: 'email',
                attr_type: 'string',
                comparison: 'eq',
                value: '',
                group: [],
              }),
            ]
          : [],
      is_custom: true,
    });
    if (onCreate) {
      await onCreate(newSegment);
    }
    onChange({
      id: newSegment.id,
      oldId: value,
    });
    setMenuResetKey((current) => current + 1);
  }

  function handleOnClear(): void {
    onChange({ id: '', oldId: value });
  }

  return (
    <UniversalSelect<Segment>
      {...additionalProps}
      async={true}
      cacheOptions={true}
      components={{
        Option: SegmentOption,
        SingleValue: SegmentSingleValue,
        MenuList: SegmentMenuList({ hideCreate, onClick: handleCreateSegment }),
      }}
      defaultOptions={true}
      disabled={disabled}
      filterOption={filterOption}
      /*
       * Keying on excludeIds forces a remount so stale cached options are
       * re-filtered when excludeIds changes; menuResetKey closes the menu
       * after creating a group.
       */
      key={`${excludeIds.join(',')}-${menuResetKey}`}
      loadOptions={loadSegmentOptions}
      name={'segment-select-simple'}
      onClear={handleOnClear}
      onInputChange={handleInputChange}
      onSelectSingle={handleSelect}
      placeholder={hideCreate ? t('Choose a group') : t('Choose or create a group')}
      value={segment}
      wrapperClassName={classNames(styles.root, wrapperClassName)}
    />
  );
}

export default observer(SegmentSelectSimple);
